/* ==========================================================================
   deOrdeño · Motor de la landing de conversión
   --------------------------------------------------------------------------
   Un solo módulo, sin dependencias. Contiene:
     1. trackEvent()  — punto único de medición (Meta Pixel, GA4, dataLayer,
                        sandbox de píxeles de Shopify → TikTok)
     2. Atribución    — captura UTM/fbclid y la pega al pedido de Shopify
     3. Quiz          — "¿Qué equipo me sirve?"
     4. Barra sticky  — precio + comprar en celular
     5. Micro-eventos — video, acordeones, secciones vistas
     6. WhatsApp      — mensaje contextual con la selección del usuario

   REGLA DE DEDUPLICACIÓN
   Shopify (canal de Meta + Customer Events) ya envía PageView, ViewContent,
   AddToCart, InitiateCheckout y Purchase, varios de ellos server-side.
   Este archivo NUNCA los vuelve a disparar. Solo emite eventos propios
   (trackCustom) y un único `Lead` estándar, con eventID, en cada clic a
   WhatsApp. Así Meta aprende de compradores sin contar doble.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.DEO_CONFIG || {};
  var LS_KEY = 'deo_attr';
  var SS_KEY = 'deo_state';

  /* ------------------------------------------------------------ utilidades */

  function noop() {}

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'e-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function readJSON(store, key) {
    try { return JSON.parse(store.getItem(key)) || null; } catch (e) { return null; }
  }

  function writeJSON(store, key, value) {
    try { store.setItem(key, JSON.stringify(value)); } catch (e) { noop(); }
  }

  /* ------------------------------------------------------- 2. ATRIBUCIÓN --
     Se guarda en localStorage con la regla "first touch gana dentro de 30
     días", que es como suelen comprar en el agro: ven el anuncio, preguntan,
     y compran días después.
     PRIVACIDAD: aquí solo entran parámetros de campaña. Nunca nombre,
     teléfono, correo ni datos financieros.
  ---------------------------------------------------------------------------*/

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid'];
  var TTL_MS = 30 * 24 * 60 * 60 * 1000;

  function capturarAtribucion() {
    var qs = new URLSearchParams(location.search);
    var nuevo = {};
    UTM_KEYS.forEach(function (k) {
      var v = qs.get(k);
      if (v) nuevo[k] = v.slice(0, 200);
    });

    var guardado = readJSON(localStorage, LS_KEY);
    var vigente = guardado && guardado.ts && (Date.now() - guardado.ts) < TTL_MS;

    if (Object.keys(nuevo).length) {
      // Hay clic nuevo desde un anuncio: manda este.
      nuevo.ts = Date.now();
      nuevo.landing = location.pathname;
      writeJSON(localStorage, LS_KEY, nuevo);
      return nuevo;
    }
    return vigente ? guardado : {};
  }

  var ATRIB = capturarAtribucion();

  /**
   * Pega la atribución al carrito como atributos del pedido.
   * Esto es lo que permite medir el embudo completo:
   *   campaña → anuncio → producto → checkout → COMPRA
   * porque los atributos quedan visibles en el pedido de Shopify.
   */
  var atribucionPegada = false;
  function pegarAtribucionAlCarrito() {
    if (atribucionPegada) return Promise.resolve();
    var claves = Object.keys(ATRIB).filter(function (k) { return k !== 'ts'; });
    if (!claves.length) return Promise.resolve();
    atribucionPegada = true;

    var attrs = {};
    claves.forEach(function (k) { attrs['_' + k] = String(ATRIB[k]); });
    if (CFG.productHandle) attrs['_deo_landing'] = CFG.productHandle;

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(noop);
  }

  /* ------------------------------------------------------- 1. trackEvent --*/

  var yaEnviado = Object.create(null);

  /**
   * Punto único de medición.
   * @param {string} nombre   Nombre del evento (p. ej. 'SelectVariant')
   * @param {object} datos    Parámetros. Solo datos de producto/campaña.
   * @param {object} opciones { unaVez:true, metaStandard:'Lead' }
   */
  function trackEvent(nombre, datos, opciones) {
    datos = datos || {};
    opciones = opciones || {};

    if (opciones.unaVez) {
      if (yaEnviado[nombre]) return;
      yaEnviado[nombre] = true;
    }

    var eventID = uuid();

    // Contexto común a todos los destinos
    var payload = Object.assign({
      event_id: eventID,
      product_name: CFG.productTitle || '',
      product_model: CFG.productHandle || '',
      product_id: CFG.productId || '',
      currency: CFG.currency || 'COP'
    }, datos, {
      source: ATRIB.utm_source || '',
      campaign: ATRIB.utm_campaign || '',
      adset: ATRIB.utm_content || '',
      ad: ATRIB.utm_term || '',
      placement: ATRIB.utm_medium || ''
    });

    // --- a) dataLayer (GTM) ------------------------------------------------
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: 'deo_' + toSnake(nombre) }, payload));

    // --- a2) Sandbox de píxeles de Shopify (TikTok y otras apps) ----------
    // Los píxeles instalados por apps (TikTok, Google) corren en el iframe
    // `web-pixels`: desde el storefront NO existe `window.ttq`. Publicamos el
    // evento con el mismo nombre que en dataLayer y un píxel personalizado de
    // Shopify (Configuración → Eventos de clientes) lo recoge y llama a
    // ttq.track('Contact') en los clics a WhatsApp. Sin esto, TikTok no se
    // entera de que la visita preguntó. Agregado el 2026-09-16.
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('deo_' + toSnake(nombre), metaSeguro(payload));
      }
    } catch (e) { noop(); }

    // --- b) GA4 directo (solo si gtag existe y no hay GTM manejándolo) -----
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'deo_' + toSnake(nombre), payload);
    }

    // --- c) Meta Pixel -----------------------------------------------------
    // Custom para todo; estándar solo cuando de verdad equivale a un evento
    // de Meta. El eventID permite deduplicar con Conversions API más adelante.
    var nombreMeta = opciones.metaStandard || ('Deo' + nombre);
    if (typeof window.fbq === 'function') {
      if (opciones.metaStandard) {
        window.fbq('track', opciones.metaStandard, metaSeguro(payload), { eventID: eventID });
      } else {
        window.fbq('trackCustom', 'Deo' + nombre, metaSeguro(payload), { eventID: eventID });
      }
    }

    // La librería de Meta no entrega en esta tienda: el envío que llega es la
    // baliza. Mismo eventID, así que nunca se cuenta doble. Ver enviarBaliza().
    enviarBaliza(nombreMeta, metaSeguro(payload), eventID);

    // --- d) Cola para Conversions API propia (si algún día se activa) ------
    if (CFG.capiEndpoint) {
      navigator.sendBeacon && navigator.sendBeacon(
        CFG.capiEndpoint,
        new Blob([JSON.stringify({ event_name: nombre, event_id: eventID, data: metaSeguro(payload), url: location.href })], { type: 'application/json' })
      );
    }

    return eventID;
  }

  function toSnake(s) {
    return s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
  }

  /** Filtro de privacidad: nada personal sale hacia Meta. */
  var PROHIBIDO = /nombre|name|tel|phone|celular|mail|correo|direccion|address|cedula|documento|mensaje|message/i;
  function metaSeguro(obj) {
    var limpio = {};
    Object.keys(obj).forEach(function (k) {
      if (PROHIBIDO.test(k)) return;
      var v = obj[k];
      if (v === '' || v == null) return;
      limpio[k] = typeof v === 'string' ? v.slice(0, 120) : v;
    });
    return limpio;
  }

  window.deoTrack = trackEvent;

  /* --------------------------------------------- Estado de lo que eligió --*/

  function estado() { return readJSON(sessionStorage, SS_KEY) || {}; }
  function guardarEstado(parcial) {
    var e = Object.assign(estado(), parcial);
    writeJSON(sessionStorage, SS_KEY, e);
    return e;
  }

  /* ------------------------------------------- 5. Secciones vistas --------*/

  function observarSecciones() {
    var objetivos = document.querySelectorAll('[data-deo-ver]');
    if (!objetivos.length || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entradas) {
      entradas.forEach(function (e) {
        if (!e.isIntersecting) return;
        var nombre = e.target.getAttribute('data-deo-ver');
        trackEvent(nombre, {}, { unaVez: true });
        io.unobserve(e.target);
      });
      // Sin `threshold`: una sección más alta que la pantalla nunca alcanza un
      // ratio alto, así que no se disparaba jamás. Le pasaba a 4 de las 22
      // —todas de más de 2.000 px medidas en móvil real—. Con esta banda
      // central el evento salta cuando la sección entra de verdad en la mitad
      // de la pantalla, sea cual sea su alto.
    }, { threshold: 0, rootMargin: '-25% 0px -25% 0px' });

    objetivos.forEach(function (t) { io.observe(t); });
  }

  /* ------------------------------------------- 5. Acordeones y video ------*/

  function conectarAcordeones() {
    document.querySelectorAll('.deo-acc').forEach(function (acc) {
      acc.addEventListener('toggle', function () {
        if (!acc.open) return;
        var s = acc.querySelector('summary');
        trackEvent(acc.getAttribute('data-deo-evento') || 'FAQOpen', {
          pregunta: s ? s.textContent.trim().slice(0, 80) : ''
        });
      });
    });
  }

  function conectarVideos() {
    document.querySelectorAll('[data-deo-video]').forEach(function (caja) {
      var boton = caja.querySelector('.deo-video__play');
      if (!boton) return;

      boton.addEventListener('click', function () {
        // El video se descarga solo aquí: nunca compite con la carga inicial.
        // Damos HLS primero (adapta el bitrate y arranca liviano en 3G) y el
        // MP4 como respaldo para los navegadores que no lo reproducen.
        var yt = caja.getAttribute('data-deo-youtube') || '';
        var mp4 = caja.getAttribute('data-deo-video') || '';
        var hls = caja.getAttribute('data-deo-video-hls') || '';
        var poster = caja.getAttribute('data-deo-poster') || '';

        // YouTube: hasta este clic no se ha pedido NADA a Google. Usamos el
        // dominio -nocookie y solo entonces insertamos el reproductor.
        if (yt) {
          var f = document.createElement('iframe');
          f.src = 'https://www.youtube-nocookie.com/embed/' + yt +
                  '?autoplay=1&rel=0&modestbranding=1&playsinline=1&hl=es';
          f.title = caja.getAttribute('data-deo-video-titulo') || 'Video del equipo';
          f.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
          f.allowFullscreen = true;
          f.loading = 'lazy';
          f.setAttribute('frameborder', '0');
          caja.innerHTML = '';
          caja.appendChild(f);
          trackEvent('VideoPlay', {
            video: caja.getAttribute('data-deo-video-nombre') || 'demo',
            fuente: 'youtube'
          });
          return;
        }

        var v = document.createElement('video');
        v.controls = true;
        v.autoplay = true;
        v.playsInline = true;
        v.preload = 'none';
        if (poster) v.poster = poster;

        var html = '';
        if (hls) html += '<source src="' + hls + '" type="application/x-mpegURL">';
        if (mp4) html += '<source src="' + mp4 + '" type="video/mp4">';
        v.innerHTML = html;

        caja.innerHTML = '';
        caja.appendChild(v);

        trackEvent('VideoPlay', { video: caja.getAttribute('data-deo-video-nombre') || 'demo' });

        var hitos = { 25: false, 50: false, 100: false };
        v.addEventListener('timeupdate', function () {
          if (!v.duration) return;
          var pct = (v.currentTime / v.duration) * 100;
          [25, 50].forEach(function (h) {
            if (pct >= h && !hitos[h]) { hitos[h] = true; trackEvent('Video' + h, {}); }
          });
        });
        v.addEventListener('ended', function () {
          if (!hitos[100]) { hitos[100] = true; trackEvent('VideoComplete', {}); }
        });
      });
    });
  }

  /* ------------------------------------------- 4. Barra sticky móvil ------*/

  function conectarSticky() {
    var barra = document.querySelector('.deo-sticky');
    if (!barra) return;

    var disparador = document.querySelector('[data-deo-sticky-desde]');
    if (!disparador || !('IntersectionObserver' in window)) {
      barra.setAttribute('data-visible', '1');
    } else {
      new IntersectionObserver(function (entradas) {
        barra.setAttribute('data-visible', entradas[0].isIntersecting ? '0' : '1');
      }, { rootMargin: '0px 0px -100% 0px' }).observe(disparador);
    }

    // El precio de la barra sigue a la variante que el usuario tenga elegida.
    var destino = barra.querySelector('[data-deo-sticky-precio]');
    var campoId = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (!destino || !campoId) return;

    var ultimo = null;
    var sincronizar = function () {
      var id = Number(campoId.value);
      if (!id || id === ultimo) return;
      ultimo = id;
      var v = (CFG.variantes || []).find(function (x) { return x.id === id; });
      if (v) destino.textContent = formatearPrecio(v.precio);
    };

    sincronizar();
    campoId.addEventListener('change', sincronizar);
    new MutationObserver(sincronizar).observe(campoId, { attributes: true, attributeFilter: ['value'] });
    // Horizon reemplaza el formulario al cambiar de variante: reintentamos.
    document.addEventListener('variant:update', sincronizar);
    setInterval(sincronizar, 700);
  }

  /** Formatea centavos de Shopify usando el formato de moneda de la tienda. */
  function formatearPrecio(centavos) {
    var valor = (centavos || 0) / 100;
    var entero = Math.round(valor).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var formato = CFG.moneyFormat || '${{amount}}';
    if (formato.indexOf('{{') === -1) return '$' + entero;
    return formato.replace(/\{\{\s*amount[^}]*\}\}/, entero);
  }

  /* ---------------------------------------------- Variantes de Shopify ----*/

  function conectarVariantes() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    form.addEventListener('change', function (ev) {
      var input = ev.target;
      if (!input || !input.name) return;
      if (!/option/i.test(input.name)) return;

      var etiqueta = (input.value || '').trim();
      guardarEstado({ variante: etiqueta });
      trackEvent('SelectVariant', { opcion: input.name, valor: etiqueta.slice(0, 60) });
    }, true);

    // Al agregar al carrito, primero pegamos la atribución para que viaje
    // con el pedido hasta el checkout.
    form.addEventListener('submit', function () { pegarAtribucionAlCarrito(); }, true);
    document.addEventListener('click', function (ev) {
      if (ev.target.closest('[name="add"], .deo-btn--comprar, .deo-btn--cod')) pegarAtribucionAlCarrito();
    }, true);
  }

  /* ------------------------------------------------------- 3. QUIZ --------*/

  function conectarQuiz() {
    var quiz = document.querySelector('[data-deo-quiz]');
    if (!quiz) return;

    var pasos = Array.prototype.slice.call(quiz.querySelectorAll('.deo-quiz__paso'));
    var barra = quiz.querySelector('.deo-quiz__barra i');
    var salidaReco = quiz.querySelector('[data-deo-quiz-reco]');
    var salidaWA = quiz.querySelector('[data-deo-quiz-wa]');
    var salidaCompra = quiz.querySelector('[data-deo-quiz-comprar]');
    var respuestas = {};
    var actual = 0;
    var iniciado = false;

    function mostrar(i) {
      pasos.forEach(function (p, n) { p.hidden = n !== i; });
      if (barra) barra.style.width = Math.round((i / pasos.length) * 100) + '%';
    }

    mostrar(0);

    quiz.addEventListener('click', function (ev) {
      var boton = ev.target.closest('.deo-quiz__op');
      if (!boton) return;

      if (!iniciado) {
        iniciado = true;
        trackEvent('StartProductQuiz', {}, { unaVez: true });
      }

      var clave = boton.closest('.deo-quiz__paso').getAttribute('data-clave');
      respuestas[clave] = boton.getAttribute('data-valor');
      guardarEstado({ quiz: respuestas });

      actual += 1;
      if (actual < pasos.length) { mostrar(actual); return; }

      // ---- Recomendación --------------------------------------------------
      var reco = recomendar(respuestas);
      if (barra) barra.style.width = '100%';
      if (salidaReco) salidaReco.innerHTML = reco.html;

      if (salidaCompra && reco.url) {
        salidaCompra.href = reco.url;
        salidaCompra.hidden = false;
      }
      if (salidaWA) salidaWA.href = enlaceWhatsApp(true);

      quiz.setAttribute('data-fin', '1');
      var res = quiz.querySelector('.deo-quiz__res');
      if (res && res.focus) res.focus();

      trackEvent('CompleteProductQuiz', {
        milking_size: respuestas.vacas || '',
        stand_count: respuestas.puestos || '',
        motor_preference: respuestas.energia || '',
        payment_type: respuestas.pago || '',
        recomendacion: reco.etiqueta
      }, { unaVez: true });
    });

    /**
     * Traduce las respuestas a una configuración real del producto.
     * Las reglas vienen del catálogo 2026 y de las variantes de Shopify:
     * la recomendación solo puede apuntar a una variante que exista.
     */
    function recomendar(r) {
      var variantes = CFG.variantes || [];
      var motorBuscado = { electricidad: 'ctric', gasolina: 'asolina', ambos: 'ixto' }[r.energia] || '';

      var elegida = null;
      if (motorBuscado) {
        elegida = variantes.find(function (v) {
          return v.disponible && v.titulo.indexOf(motorBuscado) !== -1 && /Aluminio/i.test(v.titulo);
        }) || variantes.find(function (v) {
          return v.disponible && v.titulo.indexOf(motorBuscado) !== -1;
        });
      }
      if (!elegida) elegida = variantes.find(function (v) { return v.disponible; });

      var motorTexto = {
        electricidad: 'motor eléctrico',
        gasolina: 'motor a gasolina',
        ambos: 'sistema mixto (gasolina y eléctrico)',
        nose: 'la motorización que mejor le sirva'
      }[r.energia] || '';

      var html =
        '<b>' + (CFG.productTitular || CFG.productTitle || '') + '</b>' +
        (elegida ? '<span>' + elegida.titulo + '</span>' : '') +
        '<p>' + textoPorqueQuiz(r, motorTexto) + '</p>';

      return {
        html: html,
        etiqueta: elegida ? elegida.titulo : 'sin variante',
        url: elegida ? (CFG.productUrl + '?variant=' + elegida.id) : null
      };
    }

    function textoPorqueQuiz(r, motorTexto) {
      var partes = [];
      if (r.vacas) partes.push('Para ' + r.vacas + ' este equipo le rinde el ordeño.');
      if (motorTexto) partes.push('Le recomendamos ' + motorTexto + '.');
      if (r.pago === 'contraentrega') partes.push('Puede pedirlo con pago contra entrega.');
      if (r.pago === 'financiacion') partes.push('Podemos revisar opciones de financiación con nuestros aliados.');
      return partes.join(' ');
    }
  }

  /* --------------------------------------------------- 6. WhatsApp --------*/

  function enlaceWhatsApp() {
    var e = estado();
    var q = e.quiz || {};
    var lineas = ['Hola deOrdeño 👋'];

    lineas.push('Estoy viendo el ' + (CFG.productTitular || CFG.productTitle || 'equipo') + ' en su página web.');

    if (q.vacas) lineas.push('Ordeño ' + q.vacas + '.');
    if (q.puestos && q.puestos !== 'nose') lineas.push('Me interesa ' + q.puestos + '.');
    if (q.energia) {
      lineas.push({
        electricidad: 'En la finca tengo electricidad.',
        gasolina: 'Prefiero motor a gasolina.',
        ambos: 'Quisiera el sistema mixto.',
        nose: 'Todavía no sé qué motor me conviene.'
      }[q.energia] || '');
    }
    if (q.pago) {
      lineas.push({
        contado: 'Pensaba pagarlo de contado.',
        contraentrega: 'Me interesa el pago contra entrega.',
        financiacion: 'Quiero conocer las opciones de financiación.',
        opciones: 'Quiero conocer las formas de pago.'
      }[q.pago] || '');
    }
    if (e.variante) lineas.push('La configuración que estaba mirando es: ' + e.variante + '.');

    lineas.push('Quisiera una cotización.');

    var texto = lineas.filter(Boolean).join('\n');
    return 'https://wa.me/' + (CFG.whatsapp || '') + '?text=' + encodeURIComponent(texto);
  }

  function conectarWhatsApp() {
    document.addEventListener('click', function (ev) {
      var a = ev.target.closest('[data-deo-wa]');
      if (!a) return;

      var e = estado();
      var calificado = !!(e.quiz && Object.keys(e.quiz).length >= 3) || !!e.variante;

      a.href = enlaceWhatsApp();

      // PRIVACIDAD: el mensaje de WhatsApp NO se envía a los pixeles.
      // Solo mandamos de qué sección salió y si venía calificado.
      //
      // TODO clic a WhatsApp cuenta como `Lead`. Antes solo contaba el
      // calificado (quiz de 3+ respuestas o variante elegida), y en un equipo
      // de varios millones la gente escribe ANTES de elegir configuración: ese
      // candado dejaba a Meta sin la única conversión que este negocio usa de
      // verdad. El grado de calificación viaja como parámetro del evento.
      trackEvent(calificado ? 'QualifiedWhatsAppClick' : 'WhatsAppClick', {
        origen: a.getAttribute('data-deo-wa') || 'generico',
        calificado: calificado
      }, { metaStandard: 'Lead' });
    }, true);
  }

  /* ------------------------------------------- 0. Píxel de Meta propio ----
     Shopify sirve el píxel de Meta DENTRO de su sandbox `web-pixels`, así que
     en el storefront NO existe `window.fbq`. Sin él, la rama de Meta de
     trackEvent() nunca se ejecuta: ni los eventos propios ni el `Lead` del
     WhatsApp llegaban a Meta. Verificado en producción el 2026-09-01: cero
     `Lead` en 19 días con 49.870 PageView.

     Aquí se carga el código base SOLO para eso. Deliberadamente NO se dispara
     PageView y se apaga autoConfig: los eventos estándar los sigue mandando
     el canal de Shopify (varios server-side) y duplicarlos arruinaría la
     medición. Este píxel solo transporta los eventos propios de la landing.
  ---------------------------------------------------------------------------*/

  var META_PIXEL = CFG.metaPixelId || '3265605223736788';

  function cargarPixelMeta() {
    if (!META_PIXEL || window.fbq) return;

    var n = window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];

    var t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    var s = document.getElementsByTagName('script')[0];
    if (s && s.parentNode) s.parentNode.insertBefore(t, s);
    else document.head.appendChild(t);

    try {
      window.fbq('set', 'autoConfig', false, META_PIXEL);
      window.fbq('init', META_PIXEL);
      // Sin fbq('track','PageView') a propósito: lo manda el canal de Shopify.
    } catch (e) { noop(); }
  }

  /* ------------------------------------------------- Baliza directa a Meta --
     Comprobado en producción el 2026-09-01: de este storefront NO sale ni un
     solo evento de navegador por la librería de Meta. Dos días con CERO eventos
     WEB en el píxel, mientras el canal de Shopify entregaba cientos por hora
     por servidor. Cargar fbevents.js no basta aquí: se carga, inicializa el
     píxel, y sus envíos no llegan.

     Lo que SÍ llega es una baliza de imagen contra facebook.com/tr/ — se probó
     una a mano desde esta misma página y quedó registrada en el píxel. Es el
     mismo endpoint del <noscript> que documenta Meta.

     Por eso el envío real va por aquí. Lleva el mismo eventID que la llamada a
     la librería, así que si algún día esta también entrega, Meta deduplica y
     el evento no se cuenta dos veces. Ese eventID sirve igual el día que se
     active una Conversions API propia.
  ---------------------------------------------------------------------------*/

  function enviarBaliza(evento, datos, eventID) {
    if (!META_PIXEL || !evento) return;
    try {
      var p = new URLSearchParams();
      p.set('id', META_PIXEL);
      p.set('ev', evento);
      p.set('eid', eventID);
      p.set('dl', location.href);
      if (document.referrer) p.set('rl', document.referrer);
      p.set('noscript', '1');
      Object.keys(datos || {}).forEach(function (k) {
        p.set('cd[' + k + ']', String(datos[k]).slice(0, 120));
      });
      new Image().src = 'https://www.facebook.com/tr/?' + p.toString();
    } catch (e) { noop(); }
  }

  /* ------------------------------------------------------------- Arranque -*/

  function iniciar() {
    cargarPixelMeta();
    observarSecciones();
    conectarAcordeones();
    conectarVideos();
    conectarSticky();
    conectarVariantes();
    conectarQuiz();
    conectarWhatsApp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }
})();
